import React, { useState } from 'react';
import { CloseIcon } from '../icons';

interface BookCallModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const FormInput = ({ label, placeholder, value, onChange }: { label: string; placeholder: string; value: string; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void }) => (
    <div className="flex flex-col items-start gap-1.5 flex-1">
        <label className="text-grayscale-1200 font-grotesk text-xs">{label}</label>
        <input
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            className="flex h-11 py-3 px-2.5 items-center gap-2.5 self-stretch rounded-md bg-grayscale-200 text-grayscale-1100 font-grotesk text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
    </div>
);

const topics = [
    "Open Innovation Challenges",
    "Assistance with University Connect",
    "IP Financing",
    "IP Valuation",
    "IP Strategy & Evaluation",
    "Legal, Contracts, Due Diligence",
    "Market Readiness Assessment",
    "Technology Competitive Landscape",
    "Others"
];

const TagButton = ({ text, active, onClick }: { text: string, active: boolean, onClick: () => void }) => (
    <button
        onClick={onClick}
        className={`py-1 px-4 rounded-md border text-xs font-grotesk transition-colors ${
            active
                ? 'bg-secondary-300/20 border-secondary-300 text-secondary-500 font-medium'
                : 'bg-white border-primary-200 text-grayscale-900 hover:bg-grayscale-200'
        }`}
    >
        {text}
    </button>
);


export const BookCallModal: React.FC<BookCallModalProps> = ({ isOpen, onClose }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
    
    const toggleTopic = (topic: string) => {
        setSelectedTopics(prev =>
            prev.includes(topic)
                ? prev.filter(t => t !== topic)
                : [...prev, topic]
        );
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 w-full h-full flex justify-center items-center bg-black/70 backdrop-blur-sm z-50 p-4">
            <div className="relative w-full max-w-2xl">
                {/* Desktop-only SVG background */}
                <div className="hidden sm:block absolute inset-0 -z-10">
                  <svg className="w-full h-full" viewBox="0 0 690 457" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                    <path d="M1.29802e-05 12C5.81144e-06 5.37259 5.3726 9.03043e-06 12 2.017e-05L567.909 0.000954558C576.069 0.000968274 583.67 4.14711 588.087 11.0079L592.621 18.0491C597.039 24.91 604.64 29.0561 612.8 29.056L677.999 29.0553C684.627 29.0553 689.999 34.4279 689.999 41.0553L690 444.5C690 451.127 684.627 456.5 678 456.5H12.0005C5.37309 456.5 0.000487976 451.127 0.000480808 444.5L1.29802e-05 12Z" fill="white"/>
                  </svg>
                </div>

                {/* Mobile-only simple background */}
                <div className="sm:hidden absolute inset-0 bg-white rounded-2xl -z-10" />

                {/* Desktop Close Button */}
                <button onClick={onClose} className="absolute -top-[17px] right-0 hidden sm:flex items-center gap-2 py-2.5 px-4 rounded-full bg-grayscale-1200 text-white z-10">
                    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.5 3.5L3.5 12.5" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12.5 12.5L3.5 3.5" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="font-grotesk text-sm font-medium">Close</span>
                </button>
                
                {/* Mobile Close Button */}
                <button onClick={onClose} className="sm:hidden absolute top-4 right-4 flex p-2.5 justify-center items-center gap-2 rounded-full bg-grayscale-1200 z-10">
                    <CloseIcon />
                </button>
                
                <div className="p-6 flex flex-col gap-4 max-h-[85vh] overflow-y-auto hide-scrollbar">
                    <h2 className="text-grayscale-1300 font-grotesk text-base font-medium pr-8 sm:pr-0">Need Further Assistance? Book a free call with our concierge</h2>

                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <FormInput label="Name:" placeholder="John Doe" value={name} onChange={e => setName(e.target.value)} />
                            <FormInput label="Email:" placeholder="johndoe@abccompany.com" value={email} onChange={e => setEmail(e.target.value)} />
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="text-grayscale-1200 font-grotesk text-xs">Please select which area(s) you are looking for information and assistance in:</label>
                            <div className="flex flex-wrap gap-2">
                                {topics.map(topic => (
                                    <TagButton key={topic} text={topic} active={selectedTopics.includes(topic)} onClick={() => toggleTopic(topic)} />
                                ))}
                            </div>
                        </div>
                        
                        <div className="relative">
                            <textarea
                                placeholder="Write a custom message"
                                value={message}
                                onChange={e => setMessage(e.target.value)}
                                className="h-24 w-full p-3 rounded-md bg-grayscale-200 text-secondary-text font-grotesk text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                                maxLength={600}
                            />
                            <span className="absolute bottom-2 right-2.5 text-grayscale-900 text-[10px] italic font-light">Max 600 char</span>
                        </div>
                    </div>

                    <div className="flex flex-col items-center gap-2 self-stretch">
                        <button className="flex py-2.5 px-4 justify-center items-center gap-2 self-stretch rounded-full bg-primary-500 shadow-custom-light text-grayscale-100 font-grotesk text-sm font-medium">
                            Submit Request
                        </button>
                        {/* <span className="text-grayscale-1100 text-[10px] italic">You will be taken to Calendly</span> */}
                    </div>
                </div>
            </div>
        </div>
    );
};
